/**
 * A pull request's changed files (AIOE 0.65.0, `GET /projects/v1/:id/pull-requests/:key/files`), drawn through
 * ReviewPatch. A contributor writes notes on the lines and sends them as one review comment on GitHub, as themselves
 * (the existing Comment action), the way the workbench sends a pull request's notes.
 */

import { addNote, deleteNote, drafts, editNote, renderPullRequestComment, reopenNote, type ReviewNoteItem } from '@aioe/shared';
import { Sheet } from '@heroui/react';
import { useState } from 'react';

import { routes } from '../api/client.ts';
import { useAuth } from '../auth/context.ts';
import { useLoad } from '../hooks.ts';
import { FileDiffs, LineCounts, type DiffNotes } from './review/FileDiffs.tsx';
import { Button, ErrorNote } from './ui.tsx';

export function PullRequestFilesSheet({ projectId, prKey, title, canComment, onClose, onCommented }: { projectId: string; prKey: string; title: string; canComment: boolean; onClose: () => void; onCommented?: () => void }) {
  const { account } = useAuth();
  const files = useLoad(() => routes.pullRequestFiles(projectId, prKey), [projectId, prKey]);
  const [notes, setNotes] = useState<ReviewNoteItem[]>([]);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState<number>();
  const [error, setError] = useState<string>();
  const waiting = drafts(notes);
  const send = async () => {
    if (!waiting.length) return;
    setSending(true);
    setError(undefined);
    try {
      await routes.actPullRequest(projectId, prKey, { action: 'comment', body: renderPullRequestComment(waiting, account?.name) });
      setSent(waiting.length);
      setNotes([]);
      onCommented?.();
    } catch (e) {
      setError((e as { body?: { message?: string } }).body?.message ?? (e as Error).message);
    } finally {
      setSending(false);
    }
  };
  const handlers: DiffNotes | undefined = canComment
    ? {
        notes,
        onAdd: (input) => {
          setSent(undefined);
          setNotes((n) => addNote(n, input, Date.now(), crypto.randomUUID()));
        },
        onEdit: (id, text) => setNotes((n) => editNote(n, id, text)),
        onRemove: (id) => setNotes((n) => deleteNote(n, id)),
        onReopen: (id) => setNotes((n) => reopenNote(n, id)),
        onResolve: () => undefined,
        onSendBatch: () => void send(),
      }
    : undefined;
  const f = files.data;
  return (
    <Sheet isOpen onOpenChange={(o) => !o && onClose()} placement="right">
      <Sheet.Backdrop>
        <Sheet.Content className="w-full max-w-3xl">
          <Sheet.Dialog aria-label={`Files of ${title}`}>
            <Sheet.Header>
              <Sheet.Heading className="pe-8">{title}</Sheet.Heading>
            </Sheet.Header>
            <Sheet.Body className="flex flex-col gap-3 overflow-y-auto">
              <ErrorNote error={files.error} />
              {files.loading && !f ? <p className="text-sm text-muted">Reading the files from GitHub…</p> : null}
              {f ? (
                <>
                  <div className="flex flex-wrap items-baseline gap-x-3 text-sm">
                    <span className="font-medium">
                      {f.files.length} {f.files.length === 1 ? 'file' : 'files'} changed
                    </span>
                    <LineCounts additions={f.additions} deletions={f.deletions} />
                    <a href={f.url} target="_blank" rel="noreferrer" className="text-accent hover:underline">
                      Open on GitHub
                    </a>
                  </div>
                  {canComment ? <p className="text-xs text-muted">Hover a line and press the plus to write a note on it, or drag down the margin for several lines. Your notes go to GitHub as one comment, as you.</p> : null}
                  {f.filesTruncated ? <p className="text-xs text-muted">Only the first {f.files.length} files are listed; GitHub has the rest.</p> : null}
                  {f.patchTruncated ? <p className="text-xs text-muted">Some files are shown without their lines, so the page stays quick to load.</p> : null}
                  <FileDiffs files={f.files} notes={handlers} webUrl={f.url} />
                </>
              ) : null}
            </Sheet.Body>
            {canComment && (waiting.length || sent || error) ? (
              <Sheet.Footer className="flex flex-wrap items-center justify-end gap-2">
                <ErrorNote error={error} />
                {sent && !waiting.length ? <span className="text-sm text-muted">Sent {sent} {sent === 1 ? 'note' : 'notes'} as a comment on GitHub.</span> : null}
                {waiting.length ? (
                  <>
                    <Button size="sm" variant="ghost" onPress={() => setNotes([])}>
                      Discard notes
                    </Button>
                    <Button size="sm" isPending={sending} onPress={() => void send()}>
                      Send {waiting.length} {waiting.length === 1 ? 'note' : 'notes'} as a comment
                    </Button>
                  </>
                ) : null}
              </Sheet.Footer>
            ) : null}
            <Sheet.CloseTrigger />
          </Sheet.Dialog>
        </Sheet.Content>
      </Sheet.Backdrop>
    </Sheet>
  );
}
